import React from 'react';
import type { Customer, Order } from '../../types';
import XCircleIcon from '../icons/XCircleIcon';

interface CustomerDetailModalProps {
  customer: Customer;
  orders: Order[];
  onClose: () => void;
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({ customer, orders, onClose }) => {
  // Filter orders for this customer, sorted by most recent first
  const customerOrders = orders
    .filter(order => order.customerId === customer.id)
    .sort((a, b) => b.timestamp - a.timestamp);

  // Calculate total points earned and redeemed
  const totalPointsEarned = customerOrders.reduce((sum, order) => sum + (order.pointsEarned || 0), 0);
  const totalPointsRedeemed = customerOrders.reduce((sum, order) => sum + (order.pointsRedeemed || 0), 0);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <header className="p-6 border-b flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Customer Details</h2>
            <p className="text-sm text-gray-500 mt-1">{customer.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {/* Customer Info */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-semibold">{customer.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-semibold">{customer.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Loyalty Points</p>
              <p className="font-semibold text-pink-600 text-xl">{customer.loyaltyPoints}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="font-semibold">{customerOrders.length}</p>
            </div>
          </div>

          {/* Points Summary */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3">Loyalty Points Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Total Points Earned</p>
                <p className="font-bold text-green-600 text-lg">{totalPointsEarned}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Points Redeemed</p>
                <p className="font-bold text-orange-600 text-lg">{totalPointsRedeemed}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Net Points</p>
                <p className="font-bold text-pink-600 text-lg">{totalPointsEarned - totalPointsRedeemed}</p>
              </div>
            </div>
          </div>

          {/* Order History */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">Order History</h3>
            {customerOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No orders found for this customer.</p>
            ) : (
              <div className="space-y-3">
                {customerOrders.map((order) => (
                  <div key={order.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-800">Order {order.id}</p>
                        <p className="text-xs text-gray-500">{formatDate(order.timestamp)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">${order.totalAmount.toFixed(2)}</p>
                        <p className={`text-xs font-semibold ${order.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                          {order.paymentStatus.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-1">Items</p>
                          <p className="text-sm text-gray-700">
                            {order.items.length} item{order.items.length !== 1 ? 's' : ''} • {order.orderType}
                            {order.orderType === 'dine-in' && (order.tableName || (order.tableNumber && `Table ${order.tableNumber}`)) && ` • ${order.tableName || `Table ${order.tableNumber}`}`}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          {order.pointsEarned !== undefined && order.pointsEarned > 0 && (
                            <div className="mb-1">
                              <p className="text-xs text-gray-500">Points Earned</p>
                              <p className="font-bold text-green-600">+{order.pointsEarned}</p>
                            </div>
                          )}
                          {order.pointsRedeemed !== undefined && order.pointsRedeemed > 0 && (
                            <div>
                              <p className="text-xs text-gray-500">Points Redeemed</p>
                              <p className="font-bold text-orange-600">-{order.pointsRedeemed}</p>
                            </div>
                          )}
                          {(!order.pointsEarned || order.pointsEarned === 0) && (!order.pointsRedeemed || order.pointsRedeemed === 0) && (
                            <p className="text-xs text-gray-400">No points</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomerDetailModal;

